import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';

interface Tenant {
  id?: string | number;
  uuid?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  tenant: Tenant | null;
  propertyName?: string;
  /** Pre-existing activation code for this tenant. Pass through so the
   *  template body includes it; the user generates / regenerates the code
   *  via the existing Generate flow, not in this modal. */
  activationCode?: string | null;
}

/**
 * "Email Tenant" compose dialog. No backend send — we hand off to the
 * signed-in user's own mail surface so the message goes out from their
 * address, lands in their Sent folder, and replies come back to them
 * normally. Three send paths so the user can pick whichever they have
 * signed in:
 *   • Default mail app  → mailto:  (works for Outlook desktop, Apple Mail,
 *                                   anything registered as the system handler)
 *   • Outlook on the web → https://outlook.office.com/mail/deeplink/compose
 *   • Gmail on the web   → https://mail.google.com/mail/?view=cm
 * The system never sees the message body, so there's no audit log here.
 * For audited / templated send-on-behalf, swap to an OAuth Graph/Gmail
 * integration later.
 */
export const EmailTenantModal: React.FC<Props> = ({ open, onClose, tenant, propertyName, activationCode }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Reset on each open. Body is prefilled with an activation-invite
  // template so the PM doesn't have to type one from scratch. The
  // activation code itself is minted/regenerated by the existing
  // Generate flow — this modal just consumes whatever code is already
  // on the tenant row and drops it into the message body.
  useEffect(() => {
    if (open && tenant) {
      const first = (tenant.first_name || '').trim();
      const greeting = first ? `Hi ${first},` : 'Hello,';
      const propRef = propertyName ? `for ${propertyName}` : 'for your property';
      const codeBlock = activationCode
        ? `\n\nYour activation code: ${activationCode}\n`
        : `\n\n[Activation code will be inserted here — click "Generate" on the tenant row first if no code is shown.]\n`;
      const subj = propertyName
        ? `Your Intrlock activation code for ${propertyName}`
        : 'Your Intrlock activation code';
      setSubject(subj);
      setBody(
        `${greeting}\n\n` +
        `You have been added to the Intrlock intercom system ${propRef}. ` +
        `Once you download the Intrlock mobile app, you will use the following activation code to activate your app.` +
        codeBlock +
        `\nDownload the iOS app: https://apps.apple.com/us/app/intrlock/id6765763660\n\n` +
        `If you have any questions, just reply to this email.\n\n` +
        `Thank you,\n`
      );
    }
  }, [open, tenant, propertyName, activationCode]);

  if (!open || !tenant) return null;
  const to = tenant.email || '';
  if (!to) {
    return (
      <Modal open={open} onClose={onClose} title="Email Tenant" size="md">
        <p className="text-sm text-gray-700 mb-4">This tenant doesn't have an email on file. Add one in the People page to enable sending.</p>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Modal>
    );
  }

  const subjectEnc = encodeURIComponent(subject);
  const bodyEnc = encodeURIComponent(body);
  const toEnc = encodeURIComponent(to);
  const mailtoUrl = `mailto:${to}?subject=${subjectEnc}&body=${bodyEnc}`;
  const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?to=${toEnc}&subject=${subjectEnc}&body=${bodyEnc}`;
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${toEnc}&su=${subjectEnc}&body=${bodyEnc}`;

  const tenantName = [tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || 'tenant';

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';

  const launch = (url: string) => {
    // Use _blank for webmail; for mailto: the browser hands off to the
    // OS, so the new-tab attribute is a no-op but harmless.
    window.open(url, '_blank', 'noopener');
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Email ${tenantName}`} size="lg">
      <div className="space-y-4">
        <div>
          <label className={labelCls}>To</label>
          <input type="email" value={to} readOnly className={`${inputCls} bg-gray-50 text-gray-600`} />
        </div>
        <div>
          <label className={labelCls}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className={`${inputCls} font-mono`}
          />
        </div>

        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-600 mb-2">How would you like to send it?</p>
          <p className="text-[11px] text-gray-400 mb-3">The message is sent from your own email account — Intrlock just launches the right compose surface. It goes to <span className="font-mono">{to}</span> and replies land in your inbox.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => launch(mailtoUrl)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white bg-accent-500 hover:bg-accent-600 shadow-sm"
            >
              Default Mail App
            </button>
            <button
              type="button"
              onClick={() => launch(outlookUrl)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white bg-[#0078d4] hover:bg-[#106ebe] shadow-sm"
            >
              Open in Outlook
            </button>
            <button
              type="button"
              onClick={() => launch(gmailUrl)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white bg-[#d93025] hover:bg-[#b21e16] shadow-sm"
            >
              Open in Gmail
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
};
